import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Megaphone } from "lucide-react";

const empty = {
    message: "",
    link: "",
    cta_label: "",
    theme: "brand",
    is_active: true,
    display_order: 0,
    start_at: "",
    end_at: "",
};

const THEME_PREVIEW = {
    brand: "bg-brand-500 text-white",
    accent: "bg-accent-500 text-white",
    dark: "bg-ink-900 text-white",
};

function toDatetimeLocal(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return "";
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
}
function fromDatetimeLocal(local) {
    if (!local) return null;
    const d = new Date(local);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
}

export default function TickersTab() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    const reload = () => api.get("/admin/tickers").then(({ data }) => setItems(data)).catch(() => setItems([]));
    useEffect(() => { reload(); }, []);

    const startNew = () => { setEditing({}); setForm(empty); };
    const startEdit = (row) => {
        setEditing(row);
        setForm({
            ...empty,
            ...row,
            start_at: toDatetimeLocal(row.start_at),
            end_at: toDatetimeLocal(row.end_at),
        });
    };
    const cancelEdit = () => { setEditing(null); setForm(empty); };

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((f) => ({
            ...f,
            [name]: type === "checkbox" ? checked : (name === "display_order" ? Number(value) : value),
        }));
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.message.trim()) { toast.error("Message is required"); return; }
        setSaving(true);
        try {
            const payload = {
                message: form.message.trim(),
                link: form.link?.trim() || null,
                cta_label: form.cta_label?.trim() || null,
                theme: form.theme,
                is_active: !!form.is_active,
                display_order: Number(form.display_order) || 0,
                start_at: fromDatetimeLocal(form.start_at),
                end_at: fromDatetimeLocal(form.end_at),
            };
            if (editing?.id) {
                await api.patch(`/admin/tickers/${editing.id}`, payload);
                toast.success("Ticker updated");
            } else {
                await api.post("/admin/tickers", payload);
                toast.success("Ticker created");
            }
            cancelEdit();
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete ticker "${row.message.slice(0, 40)}..."?`)) return;
        try {
            await api.delete(`/admin/tickers/${row.id}`);
            toast.success("Ticker deleted");
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div data-testid="tickers-tab">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl font-semibold">Announcement ticker</h2>
                    <p className="text-sm text-ink-700 mt-0.5">Strip shown below the navbar at the top of the page. Auto-scrolls horizontally.</p>
                </div>
                {!editing && (
                    <Button onClick={startNew} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="ticker-new-btn">
                        <Plus className="h-4 w-4 mr-1" /> New ticker
                    </Button>
                )}
            </div>

            {editing && (
                <form onSubmit={save} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 space-y-4 mb-6" data-testid="ticker-form">
                    <div>
                        <Label>Message</Label>
                        <Input name="message" value={form.message} onChange={onChange} required maxLength={240} placeholder="e.g. New batch starts 1 Jun — early-bird 15% off" className="mt-1.5 rounded-xl" data-testid="ticker-message-input" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>CTA label (optional)</Label>
                            <Input name="cta_label" value={form.cta_label || ""} onChange={onChange} maxLength={40} placeholder="e.g. Enroll now" className="mt-1.5 rounded-xl" />
                        </div>
                        <div>
                            <Label>Link (optional)</Label>
                            <Input name="link" value={form.link || ""} onChange={onChange} maxLength={300} placeholder="/membership or #courses or https://..." className="mt-1.5 rounded-xl" />
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Theme</Label>
                            <Select value={form.theme} onValueChange={(v) => setForm((f) => ({ ...f, theme: v }))}>
                                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="ticker-theme"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="brand">Brand (cyan)</SelectItem>
                                    <SelectItem value="accent">Accent (coral)</SelectItem>
                                    <SelectItem value="dark">Dark (ink)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Display order</Label>
                            <Input type="number" name="display_order" value={form.display_order} onChange={onChange} className="mt-1.5 rounded-xl" />
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Start at (optional)</Label>
                            <Input type="datetime-local" name="start_at" value={form.start_at || ""} onChange={onChange} className="mt-1.5 rounded-xl" />
                        </div>
                        <div>
                            <Label>End at (optional)</Label>
                            <Input type="datetime-local" name="end_at" value={form.end_at || ""} onChange={onChange} className="mt-1.5 rounded-xl" />
                        </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" name="is_active" checked={!!form.is_active} onChange={onChange} />
                        Active (visible on storefront)
                    </label>

                    {form.message && (
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-ink-400">Preview</Label>
                            <div className={`mt-1.5 rounded-xl overflow-hidden ${THEME_PREVIEW[form.theme] || THEME_PREVIEW.brand}`}>
                                <div className="py-2 px-4 text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                                    {form.message} {form.cta_label && form.link ? <span className="underline ml-2 font-semibold">{form.cta_label} →</span> : null}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={cancelEdit} className="rounded-full">Cancel</Button>
                        <Button type="submit" disabled={saving} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="ticker-save-btn">
                            {saving ? "Saving…" : editing?.id ? "Save changes" : "Create ticker"}
                        </Button>
                    </div>
                </form>
            )}

            <div className="space-y-3">
                {items.length === 0 && !editing && (
                    <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center text-ink-700">
                        <Megaphone className="h-6 w-6 text-ink-400 mx-auto mb-2" />
                        No tickers yet — click "New ticker" to add one.
                    </div>
                )}
                {items.map((t) => (
                    <div key={t.id} className="rounded-2xl bg-white border border-gray-100 shadow-soft p-3 flex items-center gap-3" data-testid={`ticker-row-${t.id}`}>
                        <div className={`px-3 py-1 rounded-full text-[10px] uppercase font-semibold ${THEME_PREVIEW[t.theme] || THEME_PREVIEW.brand}`}>{t.theme}</div>
                        <div className="min-w-0 flex-1">
                            <div className="font-medium text-ink-900 truncate">
                                {t.message}
                                {!t.is_active && <span className="text-[10px] uppercase ml-2 bg-gray-100 text-ink-700 rounded-full px-2 py-0.5">hidden</span>}
                            </div>
                            <div className="text-[11px] text-ink-400 mt-0.5">
                                Order {t.display_order}
                                {t.cta_label ? ` · CTA → ${t.link || "—"}` : ""}
                                {t.start_at ? ` · from ${new Date(t.start_at).toLocaleString()}` : ""}
                                {t.end_at ? ` · until ${new Date(t.end_at).toLocaleString()}` : ""}
                            </div>
                        </div>
                        <Button size="icon" variant="outline" className="rounded-full h-9 w-9" onClick={() => startEdit(t)} data-testid={`ticker-edit-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="outline" className="rounded-full h-9 w-9 text-accent-700 hover:bg-accent-50" onClick={() => remove(t)} data-testid={`ticker-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                ))}
            </div>
        </div>
    );
}
